import { useEffect, useRef, useState } from 'react'
import type { AuthenticatedUser } from '../auth'
import { googleOAuthOriginWarning, localhostDevelopmentUrl } from '../googleOAuthOrigin'
import { Icon } from './Icon'

interface GoogleCredentialResponse {
  credential: string
}

interface GoogleTokenResponse {
  access_token?: string
  error?: string
}

interface GoogleTokenClient {
  requestAccessToken: (options?: { prompt?: string }) => void
}

interface GoogleIdentityApi {
  initialize: (configuration: {
    client_id: string
    callback: (response: GoogleCredentialResponse) => void
    hd: string
    nonce: string
    auto_select: boolean
  }) => void
  renderButton: (parent: HTMLElement, options: {
    type: 'standard'
    theme: 'outline'
    size: 'large'
    text: 'continue_with'
    shape: 'rectangular'
    logo_alignment: 'left'
    locale: string
    width: number
  }) => void
  disableAutoSelect: () => void
}

declare global {
  interface Window {
    google?: { accounts: {
      id: GoogleIdentityApi
      oauth2?: {
        initTokenClient: (configuration: {
          client_id: string
          scope: string
          callback: (response: GoogleTokenResponse) => void
          error_callback?: () => void
        }) => GoogleTokenClient
      }
    } }
  }
}

interface AuthConfiguration {
  configured: boolean
  clientId?: string
  hostedDomain: string
  nonce?: string
}

interface Props {
  onAuthenticated: (user: AuthenticatedUser) => void
}

export function loadGoogleIdentityServices() {
  if (window.google?.accounts.id) return Promise.resolve()
  return new Promise<void>((resolve, reject) => {
    const existing = document.querySelector<HTMLScriptElement>('script[data-google-identity-services]')
    const script = existing ?? document.createElement('script')
    const loaded = () => resolve()
    const failed = () => reject(new Error('GOOGLE_SCRIPT_FAILED'))
    script.addEventListener('load', loaded, { once: true })
    script.addEventListener('error', failed, { once: true })
    if (!existing) {
      script.src = 'https://accounts.google.com/gsi/client?hl=pt-BR'
      script.async = true
      script.defer = true
      script.dataset.googleIdentityServices = 'true'
      document.head.append(script)
    }
  })
}

export function requestGoogleAccessToken(clientId: string, scope: string, prompt = '') {
  return new Promise<string | undefined>(resolve => {
    const oauth = window.google?.accounts.oauth2
    if (!oauth) {
      resolve(undefined)
      return
    }
    let completed = false
    const finish = (token?: string) => {
      if (completed) return
      completed = true
      window.clearTimeout(timeout)
      resolve(token)
    }
    const timeout = window.setTimeout(() => finish(), 12_000)
    try {
      const tokenClient = oauth.initTokenClient({
        client_id: clientId,
        scope,
        callback: response => finish(response.access_token),
        error_callback: () => finish(),
      })
      tokenClient.requestAccessToken({ prompt })
    } catch {
      finish()
    }
  })
}

export function requestGoogleProfileToken(clientId: string, prompt = '') {
  return requestGoogleAccessToken(clientId, 'openid email profile', prompt)
}

export function GoogleWorkspaceLogin({ onAuthenticated }: Props) {
  const buttonRef = useRef<HTMLDivElement>(null)
  const [error, setError] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const originWarning = googleOAuthOriginWarning(window.location.protocol, window.location.hostname)
  const localhostUrl = originWarning ? localhostDevelopmentUrl(window.location) : ''

  useEffect(() => {
    let active = true

    async function prepareLogin() {
      if (originWarning) {
        setError(`${originWarning} Abra o StarTracker pelo endereço localhost no computador que está executando o servidor.`)
        return
      }
      try {
        const response = await fetch('/api/auth/config', { credentials: 'include', cache: 'no-store' })
        const configuration = await response.json() as AuthConfiguration
        if (!active) return
        if (!response.ok || !configuration.configured || !configuration.clientId || !configuration.nonce) {
          setError('O login corporativo ainda não foi configurado neste ambiente.')
          return
        }
        await loadGoogleIdentityServices()
        if (!active || !buttonRef.current || !window.google?.accounts.id) return
        window.google.accounts.id.initialize({
          client_id: configuration.clientId,
          hd: configuration.hostedDomain,
          nonce: configuration.nonce,
          auto_select: false,
          callback: async ({ credential }) => {
            setSubmitting(true)
            setError('')
            try {
              const loginResponse = await fetch('/api/auth/google', {
                method: 'POST',
                credentials: 'include',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ credential, nonce: configuration.nonce }),
              })
              const result = await loginResponse.json() as { user?: AuthenticatedUser; error?: string }
              if (!loginResponse.ok || !result.user) throw new Error(result.error || 'Não foi possível entrar com esta conta.')
              onAuthenticated(result.user)
            } catch (loginError) {
              setError(loginError instanceof Error ? loginError.message : 'Não foi possível entrar com esta conta.')
              setSubmitting(false)
            }
          },
        })
        buttonRef.current.replaceChildren()
        window.google.accounts.id.renderButton(buttonRef.current, {
          type: 'standard', theme: 'outline', size: 'large', text: 'continue_with', shape: 'rectangular',
          logo_alignment: 'left', locale: 'pt-BR', width: Math.min(390, Math.max(240, buttonRef.current.clientWidth)),
        })
      } catch {
        if (active) setError('Não foi possível carregar o login do Google. Verifique sua conexão e tente novamente.')
      }
    }

    void prepareLogin()
    return () => { active = false }
  }, [onAuthenticated, originWarning])

  return <>
    {originWarning ? <a className="login-localhost-link" href={localhostUrl}>Abrir StarTracker em localhost</a> : <div className={`google-login ${submitting ? 'submitting' : ''}`} ref={buttonRef} aria-busy={submitting}/>}
    {submitting && <div className="login-progress" role="status"><span/>Validando sua conta corporativa…</div>}
    {error && <div className="login-error" role="alert"><Icon name="shield"/><span>{error}</span></div>}
  </>
}
